import { useEffect, useRef } from "preact/hooks";
import type { AvatarBitmap } from "../avatars";
import { getUserAvatar } from "../avatars";
import { usernameColorClass } from "../state";

interface AvatarProps {
  username: string;
  /** Stored bitmap from log/state, or omit to use generated avatar. */
  avatar?: AvatarBitmap | null;
  /** When provided, used by getUserAvatar to resolve stored vs generated. */
  avatarsByUsername?: Record<string, AvatarBitmap> | null;
  size?: number;
}

/**
 * User icon: shows stored avatar from state when available, otherwise a deterministic
 * generated avatar from username (see getUserAvatar / generateAvatar in avatars.ts).
 */
export default function Avatar({
  username,
  avatar,
  avatarsByUsername,
  size = 24,
}: AvatarProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const toDraw = getUserAvatar(
    username,
    avatarsByUsername ?? (avatar != null ? { [username]: avatar } : undefined),
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !toDraw) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { width: srcW, height: srcH, bits } = toDraw;
    // Draw at native resolution so we don't lose pixels; display size is set via CSS.
    canvas.width = srcW;
    canvas.height = srcH;
    ctx.imageSmoothingEnabled = false;

    const imageData = ctx.createImageData(srcW, srcH);
    const data = imageData.data;

    for (let dy = 0; dy < srcH; dy += 1) {
      for (let dx = 0; dx < srcW; dx += 1) {
        const i = dy * srcW + dx;
        const byteIndex = i >> 3;
        const bitIndex = 7 - (i & 7);
        const bit = (bits[byteIndex] >> bitIndex) & 1;
        const v = bit ? 255 : 0;
        const offset = (dy * srcW + dx) * 4;
        data[offset] = v;
        data[offset + 1] = v;
        data[offset + 2] = v;
        data[offset + 3] = 255;
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }, [toDraw]);

  const colorClass = usernameColorClass(username);

  return (
    <canvas
      ref={canvasRef}
      className={`avatar-canvas rounded-sm border-2 ${colorClass}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    />
  );
}

